import { ReactNode, useState } from 'react'
import { isStringQuality, isObjectMovie } from '../tools/typeGuards'
import utils from '../tools/utils'

import './Overview.css'
import { RemoveModerator, Visibility } from '@nine-thirty-five/material-symbols-react/sharp'
import { PlayArrow } from '@nine-thirty-five/material-symbols-react/sharp/filled'
import { Button, Card, CardActionArea, CardMedia, LinearProgress, MenuItem, Paper, Select, Tooltip } from '@iziplay/ui-kit'

interface OverviewProps {
  show: TMDB.Movie | TMDB.Episode
  clickQuality(quality: Iziplay.Downloaded): void
  progress: number
  qualities: Iziplay.Downloaded[]
  casts: TMDB.GuestStar[]
  similar: (TMDB.Movie | TMDB.Episode)[]
  available: Iziplay.Downloaded[]
  countries: string[]
  backdrop: string
  type: string
  time?: Iziplay.Time
}

type Selected = Partial<Record<Iziplay.Downloaded['quality'], Iziplay.Downloaded>>

export function Overview(props: OverviewProps) {
  const {
    show,
    clickQuality,
    progress,
    qualities,
    casts,
    similar,
    available,
    countries,
    backdrop,
    type,
    time
  } = props
  const [selected, setSelected] = useState<Selected>({})
  let countryMovieElement = null

  if (type === 'movie') {
    countryMovieElement =
      countries.length === 0 ? (
        <h3 className="durate" style={{ color: 'var(--red)' }}>
          This film hasn&apos;t been released in theaters yet
        </h3>
      ) : (
        <span className="durate">
          Available in{' '}
          {countries.length > 3 ? `${countries.length} countries` : countries.map((country, index) => (
            <span key={index}>{country} </span>
          ))}
        </span>
      )
  }

  function changeSelect(data: string) {
    if (!data) {
      return
    }
    const quality: Iziplay.Downloaded | null = JSON.parse(data)

    if (!quality) {
      return
    }

    quality.audio ||= 'VO'

    setSelected(oldSelected => ({
      ...oldSelected,
      [quality.quality]: quality
    }))
  }

  function getSelected(qual: string) {
    if (isStringQuality(qual)) {
      return selected[qual]
    }
  }

  function createQualities() {
    const optionsQualities = qualities.reduce<
      Partial<Record<Iziplay.Downloaded['quality'], ReactNode[]>>
    >((reducedQualities, quality) => {
      const optionQual = (
        <MenuItem
          key={quality.id}
          value={JSON.stringify(quality)}
          sx={{
            backgroundColor: quality.downloaded ? 'var(--green)' : ''
          }}
        >
          {quality.audio} ({quality.provider})
        </MenuItem>
      )
      reducedQualities[quality.quality] = [...(reducedQualities[quality.quality] || []), optionQual]
      return reducedQualities
    }, {})

    const localQualities = Object.keys(optionsQualities).map(qual => {
      if (!isStringQuality(qual)) return null

      const selectesQuality = getSelected(qual)

      return (
        <Paper key={qual} className="qualityCard">
          <div className="categoryTitle">{qual}</div>
          <>
            <div className="qualityInfo">Name: {selectesQuality?.name || ''}</div>
            <div className="qualityInfo">Audio: {selectesQuality?.audio || ''}</div>
            <div className="qualityInfo">Provider: {selectesQuality?.provider || ''}</div>
            <div className="qualityInfo">Seeds: {selectesQuality?.seeds || ''}</div>
          </>
          <Select className="qualitySelect" onChange={changeSelect}>
            {optionsQualities[qual]}
          </Select>
          <Button
            disabled={!selectesQuality}
            variant="contained"
            color={selectesQuality?.downloaded ? 'secondary' : 'primary'}
            style={{ marginTop: '0.8em' }}
            key={qual}
            onClick={() => selectesQuality && clickQuality(selectesQuality)}
            className={`quality${selectesQuality?.downloaded ? ' downloaded' : ''}`}
          >
            {selectesQuality?.downloaded ? 'Watch immediately' : 'Watch'}
          </Button>
        </Paper>
      )
    })

    return localQualities
  }

  const localQualities = createQualities()
  const showTitle = isObjectMovie(show) ? show.title : show.name
  const showAdult = isObjectMovie(show) ? show.adult : false
  const showDurate = isObjectMovie(show) ? show.durate : ''
  const showDate = isObjectMovie(show) ? show.release_date : show.air_date
  const showBackdrop = (isObjectMovie(show) && show.backdrop_path) || backdrop || ''
  const showPoster = (isObjectMovie(show) ? show.poster_path : show.still_path) || ''

  return (
    <div className="scrollable-nopadding movie">
      <div
        className="background"
        style={{
          backgroundImage: `url('https://image.tmdb.org/t/p/original/${showBackdrop}')`
        }}
      >
        <div className="poster_div">
          <Paper
            className={`poster${type === 'tvshow' ? ' poster-tv' : ''}`}
            elevation={3}
            style={
              showPoster
                ? {
                    backgroundImage: `url('https://image.tmdb.org/t/p/original/${showPoster}')`
                  }
                : {
                    backgroundColor: 'var(--black)',
                    backgroundImage: "url('/img/white-50.png')"
                  }
            }
          />
          <div className="titles">
            <div className="icons">
              {showAdult && <Tooltip placement="top-start" title="This is an adult movie"><RemoveModerator size={24} /></Tooltip>}
              {time && <Tooltip placement="top-start" title={`You saw ${utils.transformDurateSeconds(time.time)} at ${new Date(time.updated).toLocaleString()}`}><Visibility size={24} /></Tooltip>}
            </div>
            <div className="title name">
              {showTitle}
            </div>
            <div className="date">{showDate}</div>
            <div className="durate">{showDurate}</div>
            {countryMovieElement}
            <div className="immediatelyPlayableButtonContainer">
              {available.map((a, index) => (
                <Button
                  key={index}
                  variant="contained"
                  color="primary"
                  className="playable"
                  startIcon={<PlayArrow size={24} />}
                  onClick={() => clickQuality(a)}
                >
                  {a.quality}, {a.audio || 'VO'}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="details">
        <div className="infos">
          <div className="categoryTitle">Qualities</div>
          <LinearProgress
            style={{ marginTop: '0.4em', marginBottom: '0.8em' }}
            variant={progress > 0 ? 'determinate' : 'indeterminate'}
            value={progress * 100}
          />
          {localQualities.length === 0 && Math.ceil(progress * 100) === 100 ? (
            <div style={{ height: '10em' }}>Sorry, no file has been found...</div>
          ) : (
            <div className="qualities">{localQualities}</div>
          )}
        </div>

        {show.overview && (
          <div className="infos">
            <div className="categoryTitle">Synopsis</div>
            <div className="overview">{show.overview}</div>
          </div>
        )}

        {casts.length > 0 && (
          <div className="infos">
            <div className="categoryTitle">The Cast</div>
            <div className="castingList">
              {casts.map(people => (
                <div key={people.id} className="cast_items">
                  <Card
                    className="castingPoster"
                  >
                    <CardActionArea to={`/search?type=people&query=${encodeURIComponent(people.id)}`}>
                      <CardMedia
                        component="img"
                        height="300px"
                        image={
                          people.profile_path
                            ? `https://image.tmdb.org/t/p/w300${people.profile_path}`
                            : ''
                        }
                      />
                    </CardActionArea>
                  </Card>
                  <div className="castingName">{people.name}</div>
                  <div className="castingCharacter">{people.character}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {similar.length > 0 && (
          <div className="infos">
            <div className="categoryTitle">Similar</div>
            <div className="similarList">
              {similar.filter(sim => isObjectMovie(sim) && sim.poster_path !== null).map(sim => (
                <div key={sim.id} className="similar_items">
                  <Card
                    className="similarPoster"
                  >
                    <CardActionArea to={`/movies/${sim.id}`}>
                      <CardMedia
                        component="img"
                        height="300px"
                        image={`https://image.tmdb.org/t/p/w300${(sim as TMDB.Movie).poster_path}`}
                      />
                    </CardActionArea>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
